import { OpenInNewOutlined, SmartToyOutlined } from "@mui/icons-material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { HR, ModalDialog, MuiButton } from "@ryde/controls";

import { ConnectedAppsSectionStyled } from "./connected-apps.styled";
import { useConnectedApps } from "./use-connected-apps";

/**
 * "Connected apps" (RYDE-7833): the user's live MCP-connector grants for the CURRENT company —
 * list + per-grant disconnect. Presentation only; all state/effects live in useConnectedApps.
 * Hidden unless the company has enableMcpConnector.
 */
export const ConnectedAppsSection: FC = () => {
  const { t } = useTranslation();
  const {
    isEnabled,
    connectAgentPageUrl,
    rows,
    grantToRevoke,
    setGrantToRevoke,
    isRevoking,
    handleConfirmRevoke,
  } = useConnectedApps();

  if (!isEnabled) {
    return null;
  }

  return (
    <ConnectedAppsSectionStyled>
      <HR />
      <h3>{t("connectedApps.title")}</h3>
      <p className="sectionDescription">{t("connectedApps.description")}</p>

      {!!connectAgentPageUrl && (
        <a
          className="connectAgentLink"
          href={connectAgentPageUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-e2e="connected-apps-connect-agent"
        >
          <SmartToyOutlined className="connectAgentIcon" aria-hidden />
          <span className="connectAgentText">
            <span className="connectAgentTitle">
              {t("connectedApps.connectAgent")}
            </span>
            <span className="connectAgentHint">
              {t("connectedApps.connectAgentHint")}
            </span>
          </span>
          <OpenInNewOutlined className="connectAgentExternalIcon" aria-hidden />
          <span className="visuallyHidden">
            {t("connectedApps.opensInNewTab")}
          </span>
        </a>
      )}

      {!rows.length && (
        <div className="emptyState">{t("connectedApps.empty")}</div>
      )}

      {rows.map(
        ({ grant, permissionsLabel, connectedAtLabel, lastUsedAtLabel }) => (
          <div className="grantRow" key={grant.grantId}>
            {grant.logoUri && (
              <img
                className="grantLogo"
                src={grant.logoUri}
                alt={grant.clientName}
              />
            )}
            <div className="grantInfo">
              <span className="grantName">{grant.clientName}</span>
              <span className="grantMeta">
                {t("connectedApps.scope")}: {permissionsLabel}
              </span>
              <span className="grantMeta">
                {t("connectedApps.createdAt")}: {connectedAtLabel} ·{" "}
                {t("connectedApps.lastUsedAt")}: {lastUsedAtLabel}
              </span>
            </div>
            <MuiButton
              muiVariant="outlined"
              muiStyle="warning"
              onClick={() => setGrantToRevoke(grant)}
              e2eData="connected-apps-revoke"
            >
              {t("connectedApps.revoke")}
            </MuiButton>
          </div>
        ),
      )}

      {!!grantToRevoke && (
        <ModalDialog
          title={t("connectedApps.revokeConfirmTitle")}
          isModalOpen={!!grantToRevoke}
          closeModalClick={() => setGrantToRevoke(null)}
          saveClick={handleConfirmRevoke}
          saveButtonTitle={t("connectedApps.revoke")}
          isSaveButtonDisabled={isRevoking}
        >
          <div>
            {t("connectedApps.revokeConfirmText", {
              clientName: grantToRevoke.clientName,
            })}
          </div>
        </ModalDialog>
      )}
    </ConnectedAppsSectionStyled>
  );
};
